/**
 * Admin Categories Management
 * Provides CRUD for categories and opens CategoryPropertyManager for property editing
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Package, Plus, Edit2, Trash2, Loader2, 
  Search, X, AlertCircle, Settings, Upload, ImageIcon
} from 'lucide-react';
import axios from '../../lib/axios';
import { toast } from 'sonner';
import { API, useAuth } from '../../App';
import CategoryPropertyManager from '../CategoryPropertyManager';

const AdminCategories = () => {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showPropertyManager, setShowPropertyManager] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const fileInputRef = useRef(null);
  
  // New category form
  const [newCategory, setNewCategory] = useState({
    id: '',
    name: { en: '', nl: '', es: '' },
    icon: 'package',
    subcategories: []
  });
  const [newSubcategory, setNewSubcategory] = useState({ en: '', nl: '', es: '' });
  const [editingSubcategory, setEditingSubcategory] = useState(null); // { categoryIndex, subIndex, data }
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data || []);
    } catch (error) {
      toast.error('Failed to load categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // One-time migration: convert subcategories from strings to objects with translations
  const handleMigrateSubcategories = async () => {
    if (!window.confirm('This will migrate all subcategories to the new format with translations. Continue?')) {
      return;
    }
    
    setMigrating(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-subcategories`);
      const data = response.data;
      toast.success(`Migration complete! Migrated: ${data.migrated?.length || 0}, Skipped: ${data.skipped?.length || 0}`);
      fetchCategories(); // Refresh to see changes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
      console.error('Migration error:', error);
    } finally {
      setMigrating(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.id || !newCategory.name.en) {
      toast.error('Category ID and English name are required');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/categories`, newCategory);
      toast.success('Category added');
      setShowAddModal(false);
      setNewCategory({ id: '', name: { en: '', nl: '', es: '' }, icon: 'package', subcategories: [] });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;
    
    try {
      await axios.put(`${API}/admin/categories/${editingCategory.id}`, editingCategory);
      toast.success('Category updated');
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category? This will affect all listings using it.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const openPropertyManager = (category) => {
    setSelectedCategory(category);
    setShowPropertyManager(true);
  };

  const addSubcategory = () => {
    if (!newSubcategory.en.trim()) return;
    
    // Create subcategory object with translations
    const subId = newSubcategory.en.toLowerCase().replace(/\s+/g, '-');
    const newSubObj = {
      id: subId,
      name: {
        en: newSubcategory.en.trim(),
        nl: newSubcategory.nl.trim() || newSubcategory.en.trim(),
        es: newSubcategory.es.trim() || newSubcategory.en.trim()
      }
    };
    
    if (editingCategory) {
      setEditingCategory({
        ...editingCategory,
        subcategories: [...(editingCategory.subcategories || []), newSubObj]
      });
    } else {
      setNewCategory({
        ...newCategory,
        subcategories: [...newCategory.subcategories, newSubObj]
      });
    }
    setNewSubcategory({ en: '', nl: '', es: '' });
  };

  const openEditSubcategory = (subIndex, isEditing = false) => {
    const category = isEditing ? editingCategory : newCategory;
    const sub = category.subcategories[subIndex];
    
    // Handle both old string format and new object format
    const subData = typeof sub === 'object' 
      ? { 
          id: sub.id || sub.name?.en?.toLowerCase().replace(/\s+/g, '-') || '',
          en: sub.name?.en || '', 
          nl: sub.name?.nl || '', 
          es: sub.name?.es || '' 
        }
      : { 
          id: sub.toLowerCase().replace(/\s+/g, '-'),
          en: sub, 
          nl: '', 
          es: '' 
        };
    
    setEditingSubcategory({ 
      subIndex, 
      isEditing, 
      data: subData 
    });
    setShowSubcategoryModal(true);
  };

  const saveEditSubcategory = () => {
    if (!editingSubcategory || !editingSubcategory.data.en.trim()) return;
    
    const updatedSub = {
      id: editingSubcategory.data.id || editingSubcategory.data.en.toLowerCase().replace(/\s+/g, '-'),
      name: {
        en: editingSubcategory.data.en.trim(),
        nl: editingSubcategory.data.nl.trim() || editingSubcategory.data.en.trim(),
        es: editingSubcategory.data.es.trim() || editingSubcategory.data.en.trim()
      }
    };
    
    if (editingSubcategory.isEditing) {
      const newSubs = [...editingCategory.subcategories];
      newSubs[editingSubcategory.subIndex] = updatedSub;
      setEditingCategory({ ...editingCategory, subcategories: newSubs });
    } else {
      const newSubs = [...newCategory.subcategories];
      newSubs[editingSubcategory.subIndex] = updatedSub;
      setNewCategory({ ...newCategory, subcategories: newSubs });
    }
    
    setShowSubcategoryModal(false);
    setEditingSubcategory(null);
  };

  const removeSubcategory = (index, isEditing = false) => {
    if (isEditing) {
      setEditingCategory({
        ...editingCategory,
        subcategories: editingCategory.subcategories.filter((_, i) => i !== index)
      });
    } else {
      setNewCategory({
        ...newCategory,
        subcategories: newCategory.subcategories.filter((_, i) => i !== index)
      });
    }
  };

  // Image upload handler
  const handleImageUpload = async (categoryId, file) => {
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/admin/categories/${categoryId}/image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Category image uploaded');
      
      // Update editing category with new image URL
      if (editingCategory && editingCategory.id === categoryId) {
        setEditingCategory({
          ...editingCategory,
          image_url: response.data.image_url
        });
      }
      
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Image delete handler
  const handleImageDelete = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}/image`);
      toast.success('Image deleted');
      
      // Update editing category
      if (editingCategory && editingCategory.id === categoryId) {
        setEditingCategory({
          ...editingCategory,
          image_url: null
        });
      }
      
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    }
  };

  const filteredCategories = categories.filter(cat => 
    cat.name?.en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cat.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Property Manager Modal
  if (showPropertyManager && selectedCategory) {
    return (
      <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm overflow-y-auto">
        <div className="min-h-screen p-4">
          <div className="max-w-6xl mx-auto bg-card border border-border rounded-xl shadow-2xl">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between rounded-t-xl z-10">
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-primary" />
                <div>
                  <h2 className="text-lg font-bold">{selectedCategory.name?.en || selectedCategory.id}</h2>
                  <p className="text-sm text-muted-foreground">Manage category properties</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowPropertyManager(false);
                  setSelectedCategory(null);
                }}
                className="p-2 hover:bg-secondary rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <CategoryPropertyManager 
                category={selectedCategory} 
                onClose={() => {
                  setShowPropertyManager(false);
                  setSelectedCategory(null);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" />
            Category Management
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {categories.length} categories configured
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleMigrateSubcategories}
            disabled={migrating}
            className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 text-amber-600 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 text-sm disabled:opacity-50"
            title="Migrate subcategories to new format with translations"
          >
            {migrating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
            {migrating ? 'Migrating...' : 'Migrate Translations'}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-secondary border border-border rounded-lg"
        />
      </div>

      {/* Categories Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredCategories.map((category) => (
          <div
            key={category.id}
            className="bg-card border border-border rounded-xl p-4 hover:border-primary/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {/* Category thumbnail or icon */}
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {category.image_url ? (
                    <img 
                      src={category.image_url} 
                      alt={category.name?.en} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold">{category.name?.en || category.id}</h3>
                  <p className="text-xs text-muted-foreground">{category.id}</p>
                  {!category.image_url && (
                    <p className="text-[10px] text-amber-500 mt-0.5">No image</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditingCategory(category)}
                  className="p-1.5 hover:bg-secondary rounded-lg text-muted-foreground hover:text-foreground"
                  title="Edit Category"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  className="p-1.5 hover:bg-red-500/20 rounded-lg text-muted-foreground hover:text-red-500"
                  title="Delete Category"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Subcategories */}
            {category.subcategories?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Subcategories</p>
                <div className="flex flex-wrap gap-1">
                  {category.subcategories.slice(0, 5).map((sub, idx) => {
                    const subName = typeof sub === 'object' ? (sub.name?.en || sub.id) : sub;
                    return (
                    <span key={idx} className="text-xs px-2 py-0.5 bg-secondary rounded-full">
                      {subName}
                    </span>
                  );})}
                  {category.subcategories.length > 5 && (
                    <span className="text-xs px-2 py-0.5 bg-secondary rounded-full">
                      +{category.subcategories.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Properties Button */}
            <div className="mt-4">
              <button
                onClick={() => openPropertyManager(category)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-secondary hover:bg-secondary/80 rounded-lg text-sm font-medium"
              >
                <Settings className="w-4 h-4" />
                Manage Properties
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No categories found</p>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Add New Category</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Category ID</label>
                <input
                  type="text"
                  value={newCategory.id}
                  onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="e.g., electronics"
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Name (English)</label>
                <input
                  type="text"
                  value={newCategory.name.en}
                  onChange={(e) => setNewCategory({ ...newCategory, name: { ...newCategory.name, en: e.target.value } })}
                  placeholder="Electronics"
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Name (Dutch)</label>
                <input
                  type="text"
                  value={newCategory.name.nl}
                  onChange={(e) => setNewCategory({ ...newCategory, name: { ...newCategory.name, nl: e.target.value } })}
                  placeholder="Elektronica"
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Name (Spanish)</label>
                <input
                  type="text"
                  value={newCategory.name.es}
                  onChange={(e) => setNewCategory({ ...newCategory, name: { ...newCategory.name, es: e.target.value } })}
                  placeholder="Electrónica"
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Subcategories</label>
                <div className="space-y-2 mb-2 p-3 bg-secondary/50 rounded-lg">
                  <div>
                    <input
                      type="text"
                      value={newSubcategory.en}
                      onChange={(e) => setNewSubcategory({ ...newSubcategory, en: e.target.value })}
                      placeholder="English name (required)"
                      className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={newSubcategory.nl}
                      onChange={(e) => setNewSubcategory({ ...newSubcategory, nl: e.target.value })}
                      placeholder="Dutch"
                      className="px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      value={newSubcategory.es}
                      onChange={(e) => setNewSubcategory({ ...newSubcategory, es: e.target.value })}
                      placeholder="Spanish"
                      className="px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
                    />
                  </div>
                  <button
                    onClick={addSubcategory}
                    disabled={!newSubcategory.en.trim()}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg disabled:opacity-50 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Subcategory
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {newCategory.subcategories.map((sub, idx) => {
                    const subName = typeof sub === 'object' ? (sub.name?.en || sub.id) : sub;
                    return (
                    <span 
                      key={idx} 
                      className="flex items-center gap-1 text-xs px-2 py-1 bg-secondary rounded-full cursor-pointer hover:bg-secondary/80"
                      onClick={() => openEditSubcategory(idx, false)}
                      title="Click to edit translations"
                    >
                      {subName}
                      <button 
                        onClick={(e) => { e.stopPropagation(); removeSubcategory(idx); }} 
                        className="hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );})}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Edit Category</h3>
            
            <div className="space-y-4">
              {/* Category Image */}
              <div>
                <label className="block text-sm font-medium mb-2">Category Image</label>
                <div className="border-2 border-dashed border-border rounded-xl p-4 text-center">
                  {editingCategory.image_url ? (
                    <div className="space-y-3">
                      <div className="relative w-full h-32 rounded-lg overflow-hidden bg-secondary">
                        <img 
                          src={editingCategory.image_url} 
                          alt={editingCategory.name?.en} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="flex items-center gap-1 px-3 py-1.5 bg-secondary hover:bg-secondary/80 rounded-lg text-sm"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Replace
                        </button>
                        <button
                          onClick={() => handleImageDelete(editingCategory.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-500 hover:bg-red-500/20 rounded-lg text-sm"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="cursor-pointer py-4"
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                      ) : (
                        <>
                          <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">Click to upload image</p>
                          <p className="text-xs text-muted-foreground mt-1">800×600px recommended</p>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(editingCategory.id, file);
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Category ID</label>
                <input
                  type="text"
                  value={editingCategory.id}
                  disabled
                  className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-muted-foreground"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Name (English)</label>
                <input
                  type="text"
                  value={editingCategory.name?.en || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: { ...editingCategory.name, en: e.target.value } })}
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Name (Dutch)</label>
                <input
                  type="text"
                  value={editingCategory.name?.nl || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: { ...editingCategory.name, nl: e.target.value } })}
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Name (Spanish)</label>
                <input
                  type="text"
                  value={editingCategory.name?.es || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: { ...editingCategory.name, es: e.target.value } })}
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Subcategories</label>
                <p className="text-xs text-muted-foreground mb-2">Click on a subcategory to edit translations</p>
                <div className="space-y-2 mb-2 p-3 bg-secondary/50 rounded-lg">
                  <div>
                    <input
                      type="text"
                      value={newSubcategory.en}
                      onChange={(e) => setNewSubcategory({ ...newSubcategory, en: e.target.value })}
                      placeholder="English name (required)"
                      className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={newSubcategory.nl}
                      onChange={(e) => setNewSubcategory({ ...newSubcategory, nl: e.target.value })}
                      placeholder="Dutch"
                      className="px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      value={newSubcategory.es}
                      onChange={(e) => setNewSubcategory({ ...newSubcategory, es: e.target.value })}
                      placeholder="Spanish"
                      className="px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
                    />
                  </div>
                  <button
                    onClick={addSubcategory}
                    disabled={!newSubcategory.en.trim()}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg disabled:opacity-50 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Subcategory
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                  {editingCategory.subcategories?.map((sub, idx) => {
                    const subName = typeof sub === 'object' ? (sub.name?.en || sub.id) : sub;
                    const hasTranslations = typeof sub === 'object' && sub.name?.nl && sub.name?.es;
                    return (
                    <span 
                      key={idx} 
                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full cursor-pointer hover:bg-secondary/80 ${hasTranslations ? 'bg-green-500/20 border border-green-500/30' : 'bg-amber-500/20 border border-amber-500/30'}`}
                      onClick={() => openEditSubcategory(idx, true)}
                      title={hasTranslations ? "All translations set - click to edit" : "Missing translations - click to edit"}
                    >
                      <Edit2 className="w-3 h-3" />
                      {subName}
                      <button 
                        onClick={(e) => { e.stopPropagation(); removeSubcategory(idx, true); }} 
                        className="hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );})}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setEditingCategory(null)}
                className="flex-1 px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCategory}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subcategory Modal */}
      {showSubcategoryModal && editingSubcategory && (
        <div className="fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4">Edit Subcategory Translations</h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Subcategory ID</label>
                <input
                  type="text"
                  value={editingSubcategory.data.id}
                  disabled
                  className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-muted-foreground"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  English <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingSubcategory.data.en}
                  onChange={(e) => setEditingSubcategory({
                    ...editingSubcategory,
                    data: { ...editingSubcategory.data, en: e.target.value }
                  })}
                  placeholder="English name"
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Dutch</label>
                <input
                  type="text"
                  value={editingSubcategory.data.nl}
                  onChange={(e) => setEditingSubcategory({
                    ...editingSubcategory,
                    data: { ...editingSubcategory.data, nl: e.target.value }
                  })}
                  placeholder="Dutch name"
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Spanish</label>
                <input
                  type="text"
                  value={editingSubcategory.data.es}
                  onChange={(e) => setEditingSubcategory({
                    ...editingSubcategory,
                    data: { ...editingSubcategory.data, es: e.target.value }
                  })}
                  placeholder="Spanish name"
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowSubcategoryModal(false);
                  setEditingSubcategory(null);
                }}
                className="flex-1 px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveEditSubcategory}
                disabled={!editingSubcategory.data.en.trim()}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
